import { useState } from 'react';
import { App, Button, Spin } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { useParams } from 'react-router';
import { readableDate, type PutTemplateCalendarDto, type TemplateCalendarDto } from '@pams/shared';
import { DatePicker } from '@/components/DatePicker';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { istDateTime } from '@/lib/analyticsReport';
import { api, type ApiError } from '@/lib/api';
import { arrangementTypeLabel } from '@/lib/labels';
import { asPickerDate } from '@/lib/pickerDate';
import { queryKeys } from '@/lib/queryKeys';
import { CALENDAR_COPY, DECISIONS_COPY, TEMPLATES_COPY } from '../copy';
import { useTemplateCalendar, useTemplateSet } from '../templateQueries';
import { fillFromAnchor } from './fill';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §4b] FESTIVAL DATES FOR A YEAR — `/templates/:setKey/calendar/:year` (exit criterion 9).
 *
 *  Thirteen rows: each festival day's name and its date in the BOOKLET's year for reference, and a
 *  luxon `DatePicker` for this year. "Fill the others from this day" sets every other day at its
 *  booklet distance from the one chosen; then every row is edited on its own, and Save writes them.
 *
 *  ⚠️ THE EDITS ARE A DRAFT OVER THE SERVER'S ANSWER, NEVER A COPY SEEDED BY AN EFFECT. Until a row is
 *  touched the page shows what the server holds; the first edit takes a copy, and Save writes it back
 *  through `setQueryData` — so there is no hydration effect to overwrite a date being typed.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const TemplateCalendarPage = (): React.ReactElement => {
  const { setKey = '', year: yearParam = '' } = useParams();
  const year = Number(yearParam);
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const set = useTemplateSet(setKey);
  const calendar = useTemplateCalendar(setKey, year);
  const [draft, setDraft] = useState<Record<string, string | null> | null>(null);

  const stored: Record<string, string | null> = Object.fromEntries(
    (calendar.data?.days ?? []).map((day) => [day.dayKey, day.date]),
  );
  const dates = draft ?? stored;

  const save = useMutation({
    mutationFn: (body: PutTemplateCalendarDto) =>
      api.put<TemplateCalendarDto>(`/templates/sets/${setKey}/calendars/${String(year)}`, body),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.templates.calendar(setKey, year), result);
      setDraft(null);
      void message.success(CALENDAR_COPY.saved);
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  if (set.isPending || calendar.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }
  if (!set.data || !Number.isInteger(year)) return <EmptyState title={TEMPLATES_COPY.noSets} />;

  const days = set.data.days;
  const sourceYear = set.data.sourceYear;
  const edit = (patch: Record<string, string | null>): void => {
    setDraft({ ...dates, ...patch });
  };
  const dated = days.flatMap((day) => {
    const date = dates[day.key];
    return typeof date === 'string' ? [{ dayKey: day.key, date }] : [];
  });
  const typeLabel = arrangementTypeLabel(calendar.data?.type ?? null);

  return (
    <div className="flex flex-col gap-4" data-testid="template-calendar">
      <PageHeader
        icon={CalendarDays}
        parent={{ label: DECISIONS_COPY.parent, to: `/templates/${setKey}` }}
        title={CALENDAR_COPY.title(year)}
        actions={
          <Button
            type="primary"
            data-testid="calendar-save"
            disabled={draft === null || dated.length === 0}
            loading={save.isPending}
            onClick={() => {
              save.mutate({ days: dated });
            }}
          >
            {CALENDAR_COPY.save}
          </Button>
        }
      />

      <p className="text-sm text-muted-foreground" data-testid="calendar-type">
        {typeLabel === null ? CALENDAR_COPY.noType(year) : CALENDAR_COPY.typeIs(typeLabel)}
      </p>
      <p className="text-xs text-muted-foreground">
        {CALENDAR_COPY.fillHint} {CALENDAR_COPY.sameDate}
      </p>
      {dated.length < days.length ? (
        <p className="text-sm" data-testid="calendar-undated">
          {CALENDAR_COPY.undated(days.length - dated.length)}
        </p>
      ) : null}
      {calendar.data?.updatedBy && calendar.data.updatedAt ? (
        <p className="text-xs text-muted-foreground">
          {CALENDAR_COPY.updated(calendar.data.updatedBy.name, istDateTime(calendar.data.updatedAt))}
        </p>
      ) : null}

      <table className="w-full text-sm" data-testid="calendar-days">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="py-2 pr-4 font-normal">{CALENDAR_COPY.dayColumn}</th>
            <th className="py-2 pr-4 font-normal">{CALENDAR_COPY.sourceColumn(sourceYear)}</th>
            <th className="py-2 pr-4 font-normal">{CALENDAR_COPY.dateColumn(year)}</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {days.map((day) => {
            const date = dates[day.key] ?? null;
            return (
              <tr key={day.key} className="border-b border-border" data-testid={`calendar-row-${day.key}`}>
                <td className="py-2 pr-4">
                  <span className="flex flex-col">
                    <span>{day.name}</span>
                    {day.note ? <span className="text-xs text-muted-foreground">{day.note}</span> : null}
                  </span>
                </td>
                <td className="py-2 pr-4 text-muted-foreground">{readableDate(day.sourceDate) ?? day.sourceDate}</td>
                <td className="py-2 pr-4">
                  <DatePicker
                    format="dd LLL yyyy"
                    value={asPickerDate(date)}
                    aria-label={`${day.name} in ${String(year)}`}
                    data-testid={`calendar-date-${day.key}`}
                    onChange={(value) => {
                      edit({ [day.key]: value?.toISODate() ?? null });
                    }}
                  />
                </td>
                <td className="py-2">
                  <Button
                    size="small"
                    disabled={date === null}
                    data-testid={`calendar-fill-${day.key}`}
                    onClick={() => {
                      if (date !== null) edit(fillFromAnchor(days, day.key, date));
                    }}
                  >
                    {CALENDAR_COPY.fillFrom}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TemplateCalendarPage;
